/**
 * @fileoverview A function that provides the users API.
 *
 * @example
 * const { result } = renderHook(() => useUsersAPI())
 */

import { createApi } from '@reduxjs/toolkit/dist/query/react'

import { axiosBaseQuery } from './utils/axiosBaseQuery'

export const usersApi = createApi({
    reducerPath: 'usersApi',
    baseQuery: axiosBaseQuery({ baseUrl: `/auth` }),
    endpoints: (builder) => ({
        login: builder.mutation<
            {
                user: Novoic.API.User
                intercom: Record<string, string>
                token: string
            },
            { email: string; password: string }
        >({
            query: (body) => ({
                url: `/signin`,
                method: 'POST',
                data: body,
            }),
        }),

        verify: builder.mutation<
            { user: Novoic.API.User; intercom: Record<string, string>; token: string },
            { code: string; name: string; password: string }
        >({
            query: ({ code, name, password }) => ({
                url: `/verify/${code}`,
                method: 'POST',
                data: {
                    name,
                    password,
                },
            }),
        }),

        checkVerificationCode: builder.query<void, { code: string }>({
            query: ({ code }) => ({
                url: `/verify/${code}`,
                method: 'GET',
            }),
        }),

        requestRecovery: builder.mutation<void, { username: string }>({
            query: (body) => ({
                url: `/password/recovery`,
                method: 'POST',
                data: body,
            }),
        }),

        recoverPassword: builder.mutation<void, { password: string; token: string }>({
            query: (body) => {
                const { password, token } = body
                return {
                    url: `/password/recovery`,
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    method: 'PUT',
                    data: {
                        password,
                    },
                }
            },
        }),

        checkLinkValidity: builder.query<void, { token: string }>({
            query: ({ token }) => ({
                url: `/password/recovery`,
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }),
        }),
    }),
})

export const {
    useRequestRecoveryMutation,
    useRecoverPasswordMutation,
    useCheckLinkValidityQuery,
    useVerifyMutation,
    useCheckVerificationCodeQuery,
    useLoginMutation,
} = usersApi
